import {
  TAX_SUMMARY_MONTH_KEYS,
  isTaxSummaryCellEditable,
  parseTaxSummaryAmount,
  patchWorkbookCells,
} from "@/lib/taxSummaryWorkbookConstants";

/**
 * Parsea texto pegado desde Excel (celdas separadas por tab, filas por salto de línea).
 * Elimina filas/columnas vacías al final (Excel suele incluirlas).
 * @param {string} text
 * @returns {string[][]}
 */
export function parseExcelClipboardText(text) {
  const raw = String(text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!raw.trim()) return [];
  let lines = raw.split("\n");
  while (lines.length && lines[lines.length - 1] === "") {
    lines.pop();
  }
  if (!lines.length) return [];
  let matrix = lines.map((line) => line.split("\t"));
  const maxCols = matrix.reduce((m, row) => Math.max(m, row.length), 0);
  matrix = matrix.map((row) => {
    const next = row.slice();
    while (next.length < maxCols) next.push("");
    return next;
  });
  while (matrix.length && matrix[matrix.length - 1].every((c) => String(c).trim() === "")) {
    matrix.pop();
  }
  if (!matrix.length) return [];
  while (
    matrix[0].length
    && matrix.every((row) => String(row[row.length - 1] ?? "").trim() === "")
  ) {
    matrix = matrix.map((row) => row.slice(0, -1));
  }
  return matrix;
}

function layoutRowMeta(row) {
  const kind = row?.kind || "row";
  const mode = row?.mode || "manual";
  const rowId = row?.row_id || row?.label || "";
  return { kind, mode, rowId };
}

/**
 * Celda de monto editable para pegado (no confirmación ✓/✗ ni calculadas).
 */
export function isTaxSummaryPasteTarget(row, monthKey, readOnly, workbook) {
  const { kind, mode, rowId } = layoutRowMeta(row);
  if (kind !== "row" && kind !== "total") return false;
  if (mode === "calculated" || mode === "coeficiente" || mode === "confirmacion") return false;
  if (!rowId) return false;
  if (!isTaxSummaryCellEditable(rowId, mode, monthKey, readOnly)) return false;
  const source = workbook?.sources?.[`${rowId}.${monthKey}`];
  if (source === "calculated") return false;
  return true;
}

function normalizePasteCellValue(raw, amountMode = "money") {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return "";
  return parseTaxSummaryAmount(trimmed, { amountMode });
}

function monthKeysFrom(startMonthKey, count) {
  const start = TAX_SUMMARY_MONTH_KEYS.indexOf(startMonthKey);
  if (start < 0) return [];
  return TAX_SUMMARY_MONTH_KEYS.slice(start, start + count);
}

function collectVerticalTargets(displayLayout, startRowIdx, monthKey, count, readOnly, workbook) {
  const targets = [];
  for (let i = startRowIdx; i < (displayLayout || []).length && targets.length < count; i += 1) {
    const row = displayLayout[i];
    if (!isTaxSummaryPasteTarget(row, monthKey, readOnly, workbook)) continue;
    const { rowId, mode } = layoutRowMeta(row);
    targets.push({
      layoutRowIdx: i,
      rowId,
      monthKey,
      amountMode: mode === "coeficiente" ? "coeficiente" : "money",
    });
  }
  return targets;
}

/**
 * Planifica el pegado desde la celda activa.
 * - 1×N: fila horizontal en meses consecutivos de la misma fila.
 * - N×1: columna vertical en filas editables del mismo mes.
 * - R×C: bloque rectangular (filas editables × meses consecutivos).
 *
 * @returns {{ ok: true, patches: Array<{rowId:string, monthKey:string, value:string}>, summary: string }
 *   | { ok: false, error: string }}
 */
export function planTaxSummaryExcelPaste({
  matrix,
  startRowIdx,
  startMonthKey,
  displayLayout,
  readOnly = false,
  workbook,
} = {}) {
  const grid = Array.isArray(matrix) ? matrix : [];
  if (!grid.length || !grid[0]?.length) {
    return { ok: false, error: "No hay datos en el portapapeles para pegar." };
  }
  const rows = grid.length;
  const cols = grid[0].length;
  const layout = displayLayout || [];
  const startRow = layout[startRowIdx];
  if (!startRow || !isTaxSummaryPasteTarget(startRow, startMonthKey, readOnly, workbook)) {
    return {
      ok: false,
      error: "Coloca el cursor en una celda editable antes de pegar desde Excel.",
    };
  }

  const patches = [];

  if (rows === 1) {
    const months = monthKeysFrom(startMonthKey, cols);
    if (months.length < cols) {
      return {
        ok: false,
        error:
          `Los datos pegados no encajan: Excel trae ${cols} columna(s) y desde `
          + `${String(startMonthKey).toUpperCase()} solo caben ${months.length}. `
          + "Parámetros distintos (copia otra selección o inicia en una columna anterior).",
      };
    }
    for (let c = 0; c < cols; c += 1) {
      const mk = months[c];
      if (!isTaxSummaryPasteTarget(startRow, mk, readOnly, workbook)) {
        return {
          ok: false,
          error:
            `Los datos pegados no encajan: la columna ${String(mk).toUpperCase()} `
            + "no es editable en esta fila. Parámetros distintos.",
        };
      }
      const { rowId, mode } = layoutRowMeta(startRow);
      patches.push({
        rowId,
        monthKey: mk,
        value: normalizePasteCellValue(grid[0][c], mode === "coeficiente" ? "coeficiente" : "money"),
      });
    }
    return {
      ok: true,
      patches,
      summary: `Pegados ${cols} valor(es) en la fila (Ene–Dic / Acum.).`,
    };
  }

  if (cols === 1) {
    const targets = collectVerticalTargets(
      layout,
      startRowIdx,
      startMonthKey,
      rows,
      readOnly,
      workbook,
    );
    if (targets.length < rows) {
      return {
        ok: false,
        error:
          `Los datos pegados no encajan: Excel trae ${rows} fila(s) y desde aquí `
          + `solo hay ${targets.length} celda(s) editable(s) hacia abajo en `
          + `${String(startMonthKey).toUpperCase()}. Parámetros distintos.`,
      };
    }
    for (let r = 0; r < rows; r += 1) {
      patches.push({
        rowId: targets[r].rowId,
        monthKey: targets[r].monthKey,
        value: normalizePasteCellValue(grid[r][0], targets[r].amountMode),
      });
    }
    return {
      ok: true,
      patches,
      summary: `Pegados ${rows} valor(es) de arriba hacia abajo.`,
    };
  }

  // Bloque R×C: R filas editables (mismo mes de ancla) × C meses consecutivos.
  const vTargets = collectVerticalTargets(
    layout,
    startRowIdx,
    startMonthKey,
    rows,
    readOnly,
    workbook,
  );
  if (vTargets.length < rows) {
    return {
      ok: false,
      error:
        `Los datos pegados no encajan: Excel trae ${rows}×${cols} y hacia abajo `
        + `solo hay ${vTargets.length} fila(s) editable(s). Parámetros distintos.`,
    };
  }
  const months = monthKeysFrom(startMonthKey, cols);
  if (months.length < cols) {
    return {
      ok: false,
      error:
        `Los datos pegados no encajan: Excel trae ${rows}×${cols} y desde `
        + `${String(startMonthKey).toUpperCase()} solo caben ${months.length} columna(s). `
        + "Parámetros distintos.",
    };
  }
  for (let r = 0; r < rows; r += 1) {
    const layoutRow = layout[vTargets[r].layoutRowIdx];
    for (let c = 0; c < cols; c += 1) {
      const mk = months[c];
      if (!isTaxSummaryPasteTarget(layoutRow, mk, readOnly, workbook)) {
        return {
          ok: false,
          error:
            `Los datos pegados no encajan: en la fila «${layoutRowMeta(layoutRow).rowId}» `
            + `la columna ${String(mk).toUpperCase()} no es editable. Parámetros distintos.`,
        };
      }
      const { rowId, mode } = layoutRowMeta(layoutRow);
      patches.push({
        rowId,
        monthKey: mk,
        value: normalizePasteCellValue(grid[r][c], mode === "coeficiente" ? "coeficiente" : "money"),
      });
    }
  }
  return {
    ok: true,
    patches,
    summary: `Pegado bloque ${rows}×${cols} desde Excel.`,
  };
}

export function applyTaxSummaryPastePatches(workbook, patches) {
  return patchWorkbookCells(workbook, patches);
}
